import React from "react";
import { SWRConfig } from "swr";
import {
  APIError,
  UnauthorizedAPIError,
  requestJson,
  toErrorBody,
} from "../data/api";
import { apiFailureTags, toReportableRequest } from "../core/api_failure";
import { toRequestUrl } from "../lib/fetch_with_retry";
import { useAuth } from "./auth_portal_provider";
import * as Sentry from "@sentry/browser";

/** The one group a failed portal read is filed under. */
const PORTAL_REQUEST_FINGERPRINT = "portal-request-failed";

interface AuthPortalSWRClientProps {
  children?: React.ReactNode;
}

export function AuthPortalSWRClient(props: AuthPortalSWRClientProps) {
  const { children } = props;
  const { token, getTokenSilently, setToken } = useAuth();

  return (
    <SWRConfig
      value={{
        fetcher: async (input: RequestInfo) => {
          try {
            const responseBody = await portalFetcher(input, token);
            return responseBody;
          } catch (error) {
            if (error instanceof UnauthorizedAPIError) {
              const newToken = await getTokenSilently();
              setToken(newToken);

              const responseBody = await portalFetcher(input, newToken);
              return responseBody;
            }

            // let other errors populate up
            throw error;
          }
        },
      }}
    >
      {children}
    </SWRConfig>
  );
}

/**
 * The portal's signed read.
 *
 * Exported for its own tests — what it reports on a failing response is
 * otherwise only reachable through a rendered page behind a member session.
 */
export async function portalFetcher(input: RequestInfo, token: string) {
  const { status, body } = await requestJson(input, {
    headers: {
      "Flexspace-Client": "partners-portal",
      ...(token && {
        Authorization: `Bearer ${token}`,
      }),
    },
  });

  if (status >= 200 && status < 300) {
    return body;
  }

  const { message } = toErrorBody(body);
  const url = toRequestUrl(input);

  if (status === 401) {
    throw new UnauthorizedAPIError(message);
  }

  if (status >= 400) {
    Sentry.withScope((scope) => {
      // Redacted, not raw: a report is retained and widely readable, so it
      // must not carry whatever a member's own link or typing put in the path.
      scope.setExtras({
        input: toReportableRequest(url),
        httpStatusCode: status,
      });
      // Which endpoint failed, and how, as tags — the dimensions worth reading
      // the group apart by, without either of them naming the group.
      Object.entries(apiFailureTags({ status, url })).forEach(([tag, value]) =>
        scope.setTag(tag, value)
      );
      scope.setFingerprint([PORTAL_REQUEST_FINGERPRINT]);
      // Fixed, because the request is what varies. Interpolated, it named a
      // different group for every record a member opened — one recurring fault
      // spread so thin that no group ever showed it was recurring.
      Sentry.captureMessage("Portal: request failed", "error");
    });
  }

  // The status is what tells a caller whether the resource is merely missing,
  // so it travels with the error rather than being dropped here — and the
  // request alongside it, since whoever reports this failure sees the error and
  // nothing of the read that produced it.
  const error = new APIError(message, undefined, status);
  error.url = url;

  throw error;
}
